module Sm.UI.Components.PadView

open Browser.Types
open Fable.React
open Fable.React.Props
open MobF.React

open Sm.Service.App
open Sm.Model

open Sm.UI.Form
open Sm.UI.Activity
open Sm.UI.Pad
open Sm.UI.Timer
open Sm.UI.Autotimer

open Sm.UI.Components.Control

let MemoView (props: {| Memo: Record.Type |}) =
    let memo = props.Memo

    let isArchived = memo.Computed.ArchiveDate.IsSome

    let classes = renderClassList [
        Append "memo"
        Toggle ("is-archived", isArchived)
    ]

    div [Class classes] [
        label [Class "syntax"] (renderSyntax memo.Computed.Activity.Syntax)

        let onClick _ =
            memo.Post(Record.ToggleArchive)

        button [Class "tool"; OnClick onClick] [
            iconFor (if isArchived then Revert else IconName.Tick) []
        ]
    ]

[<ObserverComponent>]
let StepView (props: {| Step: Step; Timer: TimerModel |}) =
    let step = props.Step
    let timer = props.Timer

    let isActive =
        match timer.Computed.Active with
        | Some x when x.State.Id = step.Record.State.Id -> true
        | _ -> false

    let classes = renderClassList [
        Append "step"

        match step.Time.Status with
        | Pending when isActive -> Append "is-active-step"
        | Pending when step.IsCurrent -> Append "is-current-step"
        | Pending -> Append "is-pending-step"
        | Cancelled -> Append "is-cancelled-step"
        | Complete _ -> Append "is-complete-step"

        Toggle ("is-session-marker", step.Time.SessionMarker.IsSome)
        Toggle ("is-archived", step.Record.Computed.ArchiveDate.IsSome)
    ]

    div [Class classes] [
        aside [] [
            if isActive then
                iconFor Run [Class "run"]
            else
                match step.StartTime with
                | Some time -> span [Class "time"] [str time]
                | None -> ()
        ]

        div [Class "syntax time"] [
            yield! renderSyntax step.Record.Computed.Activity.Syntax

            span [Class $"step-indicator"] [
                match step.Time.Status with
                | Cancelled -> iconFor CrossSubtle [Class "syntax"]
                | Complete None -> iconFor IconName.Tick [Class "syntax"]
                | Complete (Some actual) ->
                    match (step.Time.Estimate - actual).Minutes with
                    | x when x >= 1 -> span [Class "syntax under"] [strf "%i" (abs x)]
                    | x when x <= -1 -> span [Class "syntax over"] [strf "%i" (abs x)]
                    | _ -> ()

                    iconFor IconName.Tick [Class "syntax"]
                | _ -> ()
            ]
        ]
    ]

[<ObserverComponent>]
let Input (props: {| Pad: Pad; Interactive: bool |}) =
    let pad = props.Pad
    let input = pad.State.Input

    let interactive = props.Interactive
    let autoFocus = Settings.current.Computed.AutoFocus
    let scrollSignal = pad.State.ScrollSignal

    let ref = Hooks.useRef<HTMLTextAreaElement>(null)
    Hooks.useEffect(
        fun () ->
            match ref.current with
            | HTMLTextAreaElement e when interactive && autoFocus && not (e.matches ":focus") -> e.focus()
            | HTMLTextAreaElement e when not interactive && (e.matches ":focus") -> e.blur()
            | _ -> ()
        , [| interactive; autoFocus; scrollSignal |]
    )

    let classList = renderClassList [
        Append "input"
        Toggle ("is-invalid", not input.Computed.IsValid)
        Toggle ("is-memo", pad.Computed.ShowMemoInput)
    ]

    div [Class classList] [
        match input.Computed.ValidationResult with
        | Ok _ -> nothing
        | Error message -> aside [] [str message]

        let onMouseDown (e: MouseEvent) = e.preventDefault()
        let onClick _ = pad.Post(IncrementTimer)
        div [Class "timer-indicator"; OnMouseDown onMouseDown; OnClick onClick; Title "Add a minute"] [
            str "..+"
        ]

        textarea [
            Title "Step input"
            Placeholder ""
            SpellCheck false
            Rows 4
            Value (input.Computed.Value |> Option.defaultValue "")
            OnKeyDown (fun e ->
                match e.key with
                | "Enter" ->
                    input.Post(TextInput.Submit)
                    e.preventDefault()
                | "Esc" | "Escape" ->
                    input.Post(TextInput.Cancel)
                    e.preventDefault()
                | _ -> ()
            )
            OnInput (fun e ->
                input.Post (TextInput.Edit e.Value)
            )
            captureRef ref
        ] []
    ]

[<ObserverComponent>]
let Header (props: {| Pad: Pad |}) =
    let pad = props.Pad

    header [] [
        let date = pad.Computed.DateDisplay

        let classList = renderClassList [
            Append "date"
            Toggle ("is-today", date.IsToday)
        ]

        span [Class classList] [str date.Text]
        span [Class "spacer"] []

        if pad.State.DateInput.IsNone then
            renderButton pad.Computed.ShowPreviousDate pad.Post
            renderButton pad.Computed.ShowNextDate pad.Post
            renderButton pad.Computed.ShowToday pad.Post

        renderButton pad.Computed.ShowCalendar pad.Post
    ]

[<ObserverComponent>]
let PadView (props: {| Pad: Pad; Timer: TimerModel; Autotimer: Autotimer |}) =
    let pad = props.Pad
    let timer = props.Timer
    let autotimer = props.Autotimer

    let ref = Hooks.useRef<HTMLDivElement>(null)
    Hooks.useLayoutEffect(
        fun () -> (
            match ref.current with
            | HTMLDivElement e -> e.scrollTop <- e.scrollHeight
            | _ -> ()
        ),
        [| pad.State.ScrollSignal |]
    )

    let classList = renderClassList [
        Append "pad-view"
        Toggle ("is-empty", pad.Computed.Entries.IsEmpty)
    ]

    div [Class classList] [
        Header {|
            Pad = pad
        |}

        match pad.State.DateInput with
        | Some input ->
            div [Class "calendar-view date-picker"] [
                header [] [
                    div [Class "control month"] [
                        renderButton input.Computed.PreviousMonth input.Post
                        renderButton input.Computed.NextMonth input.Post

                        label [] [
                            span [Class "month"] [str input.Computed.MonthLabel]
                            span [Class "year"] [str input.Computed.YearLabel]
                        ]

                        renderButton input.Computed.Current input.Post
                    ]

                    DayNames ()
                ]

                div [Class "days"] [
                    let onSelect date =
                        let msg = input.State.Config.AcceptMessage date
                        pad.Post(msg)

                    for day in input.Computed.Days do
                        DayView {|
                            Day = day
                            OnSelect = onSelect
                            ShowMarks = false
                        |}
                ]

                div [Class "entries scroll"] [
                    for memo in pad.Computed.PendingMemos do
                        MemoView {| Memo = memo |}
                ]
            ]
        | _ ->
            div [Class "entries scroll"; captureRef ref] [
                match pad.Computed.Entries with
                | [] when pad.Computed.IsToday ->
                    div [Class "message is-info"] [
                        iconFor Zzz []
                        blockquote [] [str "To achieve great things, two things are needed: a plan, and not quite enough time"]
                        cite [] [str "Leonard Bernstein"]
                    ]
                | entries ->
                    for entry in entries do
                        match entry with
                        | Memo memo -> MemoView {| Memo = memo |}
                        | Step step -> StepView {| Step = step; Timer = timer |}

                    match pad.Computed.Estimate with
                    | None -> nothing
                    | Some estimate ->
                        aside [Class "estimate"] [
                            iconFor ArrowRight []
                            span [] [str estimate]
                        ]
            ]

            div [Class "panel"] [
                if pad.Computed.IsToday then
                    Input {|
                        Pad = pad
                        Interactive = true
                    |}

                div [Class "autotimer-bar"] [
                    autotimer.Post |> renderButton autotimer.Computed.RunOne
                    autotimer.Post |> renderButton autotimer.Computed.RunTwo
                    autotimer.Post |> renderButton autotimer.Computed.RunThree
                    autotimer.Post |> renderButton autotimer.Computed.RunFour
                ]

                TimerView.TimerView {|
                    Timer = timer
                |}
            ]
    ]
